import { Container, Stack, Typography, Box, Popper, Paper, styled, alpha, List, ListItem, ListItemButton, ListItemIcon, Avatar, ClickAwayListener, Chip, ListItemText, Button, IconButton } from "@mui/material";
import InputUnstyled from '@mui/base/InputUnstyled';
import { useDebouncedCallback } from "use-debounce";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { Employee } from "../../types/Employee";
import { queryEmployees } from "../../utils/queryEmployees";
import { queryMessageThreads } from "../../utils/queryMessages";
import { useAppSelector } from "../../store";
import { SendRounded } from "@mui/icons-material";
import { createMessageThread, sendMessage } from "../../utils/sendMessage";
import { useLocation, useNavigate } from "react-router-dom";
import { queryEmployee } from "../../utils/queryEmployee";

const SearchBox = styled(Paper)(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  minHeight: 56,
  flexGrow: 1,
  flexWrap: 'wrap',
  justifyContent: 'flex-start',
  padding: theme.spacing(1),
}));

const CleanInput = styled('input')(({ theme }) => ({
  ...theme.typography.body1,
  backgroundColor: 'transparent',
  border: 'none',
  color: theme.palette.text.primary,
  outline: 'none',
}));

interface StartConversationBoxProps {

};
export const StartConversationBox = (props: StartConversationBoxProps) => {
  const auth = useAppSelector((s) => s.auth);
  const me = auth as unknown as Employee;
  
  const { state } = useLocation();

  const navigate = useNavigate();

  const searchRef = useRef<HTMLDivElement>();
  const [searchAnchorEl, setSearchAchorEl] = useState<HTMLDivElement>();
  const [search, setSearch] = useState('');
  const [searchResult, setSearchResult] = useState<Employee[]>([]);
  const [users, setUsers] = useState<Employee[]>([]);
  const [start, setStart] = useState(false);
  const [message, setMessage] = useState('');

  // Search functions
  const searchDebounced = useDebouncedCallback<(value: string) => void>((value) => {
    setSearchResult([]);
    if (value.trim() !== '') {
      queryEmployees(value).then((value) => {
        setSearchResult(value);
      });
    }
  }, 1000);

  const searchOnChanged = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.currentTarget.value);
    searchDebounced(event.currentTarget.value);
  };

  const handleSearchItemOnClick = (e: Employee) => {
    if (users.findIndex((v) => v.idNumber === e.idNumber) === -1) {
      // Not yet in users
      setUsers([...users, e]);
      setSearch('');
    }
  };
  
  const handleStartConversationOnClick = () => {
    const members = [me, ...users];
    queryMessageThreads(me.idNumber, members.map((u) => u.idNumber)).then((threads) => {
      if (threads.length === 0) {
        setStart(true);
      } else {
        const membersId = members.map(e => e.idNumber);
        for (let i = 0; i < threads.length; i++) {
          const t = threads[i];
          if (t.membersId.length === members.length) {
            if (t.membersId.every((e) => membersId.includes(e))) {
              navigate(`/messages/t/${t.id}`);
              break;
            }
          }
        }
      }
    });
  };

  const handleSendOnClick = () => {
    const members = [me, ...users];
    if (start) {
      
    }
  };

  useEffect(() => {
    if (!Boolean(searchAnchorEl)) {
      setSearch('');
    }
  }, [searchAnchorEl]);

  useEffect(() => {
    if (users.length === 0) {
      setStart(false);
    }
  }, [users])

  useEffect(() => {
    if ((state as any).idNumber) {
      queryEmployee((state as any).idNumber).then((value) => {
        if (value) {
          setUsers([value]);
          setStart(true);
        }
      });
    }
  }, []);

  return (
    <Container disableGutters sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
      <Stack sx={{ flexGrow: 1 }}>
        <ClickAwayListener onClickAway={() => setSearchAchorEl(undefined)}>
          <div>
            <Stack 
              component={SearchBox} 
              ref={searchRef} 
              direction="row"
              spacing={0.5}
            >
              <Typography sx={{ marginRight: 2 }}>To:</Typography>
              {users.map((u) => (
                <Chip
                  key={`u-avatar-chip-${u.uid}`}
                  avatar={<Avatar src={u.picture} />}
                  label={u.name}
                  onDelete={() => setUsers(users => users.filter((e) => e.uid !== u.uid)) }
                />
              ))}
              <InputUnstyled
                components={{
                  Input: CleanInput,
                }}
                value={search}
                onFocus={() => setSearchAchorEl(searchRef.current)}
                onChange={searchOnChanged} />
            </Stack>
            <Popper open={Boolean(searchAnchorEl)} anchorEl={searchAnchorEl} placement="bottom-start">
              <Paper>
                <List>
                  {searchResult.filter((v) => !users.map((u) => u.idNumber).includes(v.idNumber)).map((e) => {
                    return (
                      <ListItem key={e.uid} disableGutters disablePadding sx={{ width: 400 }}>
                        <ListItemButton onClick={() => handleSearchItemOnClick(e)}>
                          <ListItemIcon>
                            <Avatar src={e.picture} />
                          </ListItemIcon>
                          <ListItemText
                            primary={e.idNumber}
                            secondary={e.name} />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Paper>
            </Popper>
          </div>
        </ClickAwayListener>
        <Box sx={{ flexGrow: 1 }}>
        </Box>
        <Box>
          {start ? (
            <Stack component={Paper} direction="row" sx={{ flexGrow: 1, padding: 1 }}>
              <CleanInput sx={{ flexGrow: 1, }} value={message} onChange={(e) => setMessage(e.target.value)} />
              <IconButton color="primary" onClick={handleSendOnClick}>
                <SendRounded />
              </IconButton>
            </Stack>
          ) : (
            <Button fullWidth size="large" disabled={users.length === 0} onClick={handleStartConversationOnClick}>
              Start Conversation
            </Button>
          )}
        </Box>
      </Stack>
    </Container>
  );
};